/**
 * Identifier handling for API payloads.
 *
 * Ids are uuid strings. Before that they were sequential integers, and every
 * feature carried its own private `resolveNumber` to coerce whatever the API
 * returned into one. Those copies all had the same shape and the same flaw: a
 * value they could not parse came back as `null`, and callers routinely wrote
 * `?? 0`, turning a missing id into a real-looking one that silently matched the
 * wrong row — or no row at all.
 *
 * `resolveId` keeps the tolerant reading (a number from an older payload is
 * still accepted) but never invents a value: unusable input is `null`, and the
 * caller has to decide what that means.
 */

/** The uuid a defaulted or empty field serialises to. It is never a real row. */
const NIL_ID = '00000000-0000-0000-0000-000000000000';

/**
 * Reads an identifier out of an untyped API value.
 *
 * Returns `null` — never a placeholder — when the value is absent, blank, or the
 * nil uuid. Numbers are accepted so a payload from an older service keeps
 * working, but the result is always a string.
 */
export function resolveId(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0 || trimmed === NIL_ID) {
      return null;
    }
    return trimmed;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  return null;
}

/**
 * Reads a list of identifiers, dropping the entries that are not usable.
 */
export function resolveIds(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(resolveId)
    .filter((id): id is string => id !== null);
}

/**
 * True when the value can be used as an identifier.
 *
 * Prefer this over a truthiness check: `'0'` is falsy-looking to a reader used
 * to numeric ids but is a perfectly ordinary string, while the nil uuid looks
 * like a real id and is not one.
 *
 * It is a type predicate, so a guarded `string | undefined` narrows to `string`
 * and the caller does not need a second, redundant null check to satisfy the
 * compiler.
 */
export function isValidId(value: unknown): value is string {
  return resolveId(value) !== null;
}

/**
 * Compares two identifiers coming from different sources — a route parameter is
 * a string, a signal may hold one read from a payload — without either side
 * having to be normalised first.
 */
export function sameId(a: unknown, b: unknown): boolean {
  const left = resolveId(a);
  return left !== null && left === resolveId(b);
}
